import * as React from 'react';
import { Link as GatsbyLink } from 'gatsby';

export interface LinkProps {
  children?: React.ReactNode;
  href?: string;
  download?: boolean;
}

// Utility functions

/**
 * Removes any additional `/docs/` prefix that might be added by the MDX Provider
 */
function formatPath(path: string) {
  return path.replace('/docs/', '/');
}

/**
 * Checks if a string has a supported file extension
 */
function hasFileExtension(url: string): boolean {
  // Extract portion after the last slash, and before any query or hash
  const fileName = url.split('/').pop()?.split(/[?#]/)[0];
  const extension = fileName?.split('.').pop()?.toLowerCase();

  return fileExtensions.includes(`.${extension}`);
}

/** Supported file types for external links */
const fileExtensions = [
  // Documents
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.json',
  '.yml',
  '.yaml',
  // Images
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.svg',
  '.webp',
  '.ico',
  // Archives
  '.zip',
  '.rar',
  '.tar',
  '.gz',
  // Audio & Video
  '.mp3',
  '.mp4',
  '.avi',
  '.mkv',
  '.flv',
  '.wmv',
  '.mov',
  '.wav',
  // Fonts
  '.ttf',
  '.otf',
  '.woff',
  '.woff2',
  '.eot',
];

/**
 * A custom link that parses Markdown for on-site vs off-site URLs
 * and formats as a Gatsby link or standard anchor tag.
 */
export const Link = ({ children, download, href, ...rest }: LinkProps) => {
  if (!href) return null;
  const isFile = hasFileExtension(href);
  const isOnPage = href.startsWith('#');
  const isLocal = href.startsWith('/'); // Located within this codebase

  if (isLocal && !isFile) {
    return <GatsbyLink to={formatPath(href)}>{children}</GatsbyLink>;
  }

  let attributes: React.AnchorHTMLAttributes<HTMLAnchorElement> = {
    href,
  };

  if (!isOnPage) {
    attributes.href = href;
    attributes.target = !isFile || (isFile && !download) ? '_blank' : undefined;
    attributes.rel = !isFile ? 'noopener noreferrer' : undefined;
    attributes.download = download ? '' : undefined;
  }

  return (
    <a {...attributes} {...rest}>
      {children}
    </a>
  );
};
